import _ from 'lodash';
import numeral from "numeral";

import { DealRoomChangeRequest, DealRoomSectionChangeRequest } from 'src/types/dealRoom';
import { secondsToMinutes } from './util';

export function persistSortPreference(sortBy: string, orderBy: string) {
  const sort = {
    sortBy: sortBy,
    orderBy: orderBy
  }

  window.localStorage.setItem("dealRoomSort", JSON.stringify(sort));
}


export function getSortPreference() {
  const sort = window.localStorage.getItem("dealRoomSort");
  return sort ? JSON.parse(sort) : { sortBy: 'name', orderBy: 'asc' };
}

export const shouldNotifyOnChange = (source, target) => {
  const sourceCollection = {
    id: _.get(source, 'id'),
    name: _.get(source, 'name'),
    assets: getAssetsInCollection(source),
    description: _.get(source, 'description')
  }

  const targetCollection = {
    id: _.get(target, 'id'),
    name: _.get(target, 'name'),
    assets: getAssetsInCollection(target),
    description: _.get(target, 'description')
  }

  return !_.isEqual(sourceCollection, targetCollection);
}

const getAssetsInCollection = (collection) => {
  const assetIds: string[] = [];

  const sections = _.get(collection, 'sections');

  if (sections && sections.length > 0) {
    sections.forEach(section => {
      if (section.loaded && section.item.assets) {
        section?.item?.assets?.items?.forEach(asset => {
          assetIds.push(asset.id);
        })
      }
    })
  } else {
    const assets = _.get(collection, 'assets');
    if (assets && assets.length > 0) {
      assets.forEach(asset => {
        assetIds.push(asset.id)
      })
    }
  }

  return assetIds.sort();
}

export const calculateUpdateRequest = (source, target) => {
  const changes: DealRoomChangeRequest = {};

  if (!_.isEqual(source.subject, target.subject) && target.subject !== '') {
    changes.name = target.subject;
  }

  if (!_.isEqual(source.description, target.description)) {
    changes.description = target.description;
  }

  if (!_.isEqual(source.meetingLink, target.meetingLink)) {
    changes.meetingLink = target.meetingLink;
  }

  if (!_.isEqual(source.banner, target.banner)) {
    changes.banner = { ...target.banner };
    const sourceBackground = _.get(source, 'banner.background.id');
    const targetBackground = _.get(target, 'banner.background.id');

    if (sourceBackground !== targetBackground) {
      _.set(changes, 'banner.background', targetBackground);
    } else {
      _.set(changes, 'banner.background', null);
    }
  }


  if (!_.isEqual(source.customerName, target.customerName)) {
    changes.customerName = target.customerName;
  }

  if (!_.isEqual(source.customerLogo, target.customerLogo)) {
    changes.customerLogo = _.get(target, 'customerLogo.id') || null;
  }

  if (!_.isEqual(source.companyLogo, target.companyLogo)) {
    changes.companyLogo = _.get(target, 'companyLogo.id') || null;
  }

  if (!_.isEqual(source.viewerType, target.viewerType)) {
    changes.viewerType = target.viewerType;
  }

  if (!_.isEqual(source.assetViewer, target.assetViewer)) {
    changes.assetViewer = target.assetViewer;
  }

  if (!_.isEqual(source.opportunity, target.opportunity)) {
    changes.opportunity = target.opportunity;
  }

  const sourceAssets = prepareAssetUpdate(source.assets || []);
  const targetAssets = prepareAssetUpdate(target.assets || []);
  if (!_.isEqual(sourceAssets, targetAssets)) {
    const deletedAssets = _.differenceBy(sourceAssets, targetAssets, 'id')
    const deletedAssetsIds = deletedAssets.map(item => _.get(item, "id", ""));
    if (_.size(deletedAssetsIds) > 0) {
      changes.deletedAssets = deletedAssetsIds;
    }
    changes.assets = targetAssets;
  }


  if (target.sections && target.sections.length > 0) {
    const sourceSections = source.sections || [];

    changes.sections = target.sections.map(section => {

      //Find the section in source sections
      const sourceSection = sourceSections.find(item => {
        return item.item?.id == section.item?.id;
      });

      return calculateSectionChanged(sourceSection, section);
    }).filter(change => change != null);
  }

  if (source.sections && source.sections.length > 0) {
    const targetSections = target.sections || [];

    changes.deletedSections = source.sections.filter(section => {
      const found = targetSections.find(item => {
        return section.item?.id == item.item?.id;
      });

      return found == null;
    }).map(item => item.item.id).filter(item => item != null);
  }

  return changes;
}

const prepareAssetUpdate = (assets) => {
  if (!assets || _.size(assets) === 0) {
    return [];
  }

  return assets.map((asset, index) => {
    return {
      id: asset.id,
      order: index + 1,
      assetMetadata: asset.metadata
    };
  });
}


export const calculateSectionChanged = (source, target) => {
  const targetAssets = prepareAssetUpdate(target.item?.assets?.items || []);
  if (!source) {
    return {
      id: target.item?.id,
      name: target.item?.name,
      assets: targetAssets
    }
  }

  const sourceAssets = prepareAssetUpdate(source.item?.assets?.items || []);

  const changes: DealRoomSectionChangeRequest = {
    id: target.item?.id,
  };

  if (!_.isEqual(source.item?.name, target.item?.name)) {
    if (target.item?.name && target.item?.name.trim() !== "") {
      changes.name = target.item?.name;
    }
  }

  if (!_.isEqual(sourceAssets, targetAssets)) {
    const deletedAssets = _.differenceBy(sourceAssets, targetAssets, 'id')
    const deletedAssetsIds = deletedAssets.map(item => _.get(item, "id", ""));
    if (_.size(deletedAssetsIds) > 0) {
      changes.deletedAssets = deletedAssetsIds;
    }

    changes.assets = targetAssets;
  }

  return changes;
}

export enum SmartCardTypes {
  DEAL_PROBABILITY = "DEAL_PROBABILITY",
  ACTIVE_PEOPLE = "ACTIVE_PEOPLE",
  MESSAGE_LIST = "MESSAGE_LIST",
  PARTICIPANT_ENGAGEMENT = "PARTICIPANT_ENGAGEMENT",
  RECENT_UPLOADS = "RECENT_UPLOADS",
  NEW_PARTICIPANT = "NEW_PARTICIPANT",
  MAP_DEAL = "MAP_DEAL",
  DEALROOM_ENGAGEMENT = "DEALROOM_ENGAGEMENT"
}

export enum SmartCardsOrder {
  DEAL_PROBABILITY = 0,
  MAP_DEAL = 1,
  ACTIVE_PEOPLE = 2,
  MESSAGE_LIST = 3,
  PARTICIPANT_ENGAGEMENT = 4,
  RECENT_UPLOADS = 5,
  NEW_PARTICIPANT = 6,
}

export enum DefaultSmartCardTypes {
  DEAL_PROBABILITY = "DEAL_PROBABILITY",
  ACTIVE_PEOPLE = "ACTIVE_PEOPLE",
  MESSAGE_LIST = "MESSAGE_LIST",
  MAP_DEAL = "MAP_DEAL",
}

const getDefaultSmartCards = (isDealInsightsEnabled: boolean, isDealMapped: boolean) => {
  return [
    {
      type: SmartCardTypes.DEAL_PROBABILITY,
      title: isDealInsightsEnabled ? "Deal details ready!" : "view deal insights",
      subtext: isDealInsightsEnabled ? "Map a deal to see Win Probability and Pipeline Progress in action." : "see win probability and pipeline progress — ask us how!",
      ctaText: isDealInsightsEnabled ? "connect deal" : "learn more",
    },
    {
      type: SmartCardTypes.MAP_DEAL,
      title: isDealMapped ? "update deal" : "map a deal",
      subtext: isDealMapped ? "switch to a different deal to match the right opportunity." : "map to a deal to view all deal related insights, all in one place.",
      ctaText: isDealMapped ? "update now" : "map now",
    },
    {
      type: SmartCardTypes.ACTIVE_PEOPLE,
      title: "No one is online right now.",
      subtext: "",
      ctaText: "start a conversation",
    },
    {
      type: SmartCardTypes.MESSAGE_LIST,
      title: "You're all caught up with the messages!",
      subtext: "",
      ctaText: "",
    },
  ];
}

export const transformSmartCards = (cards: any[], isDealMapped: boolean, isDealInsightsEnabled: boolean, dealRoomOwner: string, isEditing: boolean) => {
  const defaultSmartCardsToBeAppended = getDefaultSmartCards(isDealInsightsEnabled, isDealMapped).filter(card => {
    return !_.some(cards, { type: card.type })
  });

  if (isEditing) {
    return [defaultSmartCardsToBeAppended[1]];
  }

  cards = _.map(cards, (card) => {
    const type = _.get(card, "type", "");

    const doubleWidth =
      type === SmartCardTypes.RECENT_UPLOADS ||
      (type === SmartCardTypes.MESSAGE_LIST && _.get(card, "unReadMessages", 0) > 0) ||
      type === SmartCardTypes.PARTICIPANT_ENGAGEMENT ||
      type === SmartCardTypes.NEW_PARTICIPANT;

    const getSmartCardContent = () => {
      switch (type) {
        case SmartCardTypes.DEAL_PROBABILITY:
          return {
            title: isDealInsightsEnabled ? `${numeral(_.get(card, "prediction", 0)).format("(0)")}%` : "",
            subtext: isDealInsightsEnabled ? `${_.get(card, 'stage')}` : "",
            ctaText: isDealInsightsEnabled ? "more details" : "click here to know more",
          }

        case SmartCardTypes.ACTIVE_PEOPLE:
          let activePeopleSubtext = "";
          let activePeopleCtaText = "chat now";
          const activePeopleCard = _.find(cards, { type: SmartCardTypes.ACTIVE_PEOPLE });
          const activePeople = _.get(activePeopleCard, "chatUsers", []);
          if (_.size(activePeople) > 1) {
            activePeopleSubtext = `${_.get(activePeople[0], 'firstName')} & ${_.size(activePeople) - 1} other people are active now, start a conversation`;
          } else if (_.size(activePeople) === 1) {
            activePeopleSubtext = `${_.get(activePeople[0], 'firstName')} is active now, start a conversation`;
          } else {
            activePeopleSubtext = "no one is online right now!"
            activePeopleCtaText = "start a conversation";
          }

          return {
            title: "",
            ctaText: activePeopleCtaText,
            subtext: activePeopleSubtext,
          }

        case SmartCardTypes.MESSAGE_LIST:
          return {
            title: `you've got new messages! (${_.get(card, "unReadMessages", 0)})`,
            ctaText: "view now",
            subtext: "Don't miss out on important conversations!",
          }

        case SmartCardTypes.PARTICIPANT_ENGAGEMENT:
          return {
            title: `participants spent ~${secondsToMinutes(_.get(card, "timeSpent", 0))}m on your recently uploaded asset`,
            ctaText: "more details",
            subtext: "",
          }

        case SmartCardTypes.RECENT_UPLOADS:
          return {
            title: `recently uploaded assets (${_.get(card, 'assetCount', 0)})`,
            ctaText: "more details",
            subtext: "",
          }

        case SmartCardTypes.NEW_PARTICIPANT:
          let newParticipantSubtext = `${_.get(card, 'totalPeople')} user${_.get(card, 'totalPeople') > 1 ? 's were' : ' was'} recently added by ${dealRoomOwner ?? "Deal Room Owner"}`;
          return {
            title: "new external stakeholders joined!",
            ctaText: "view now",
            subtext: newParticipantSubtext,
          }

        default:
          return null;
      }
    }

    const smartCardContent = getSmartCardContent();

    return {
      width: doubleWidth ? 2 : 1,
      height: 1,
      ...smartCardContent,
      ...card
    };
    
  });
  
  cards = [
    ...defaultSmartCardsToBeAppended,
    ...cards
  ];

  const sortedCards = cards.sort((a, b) => {
    return SmartCardsOrder[a.type as keyof typeof SmartCardsOrder] -
           SmartCardsOrder[b.type as keyof typeof SmartCardsOrder];
  });
  return sortedCards;
}